import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

import { ProvenanceField, type Provenance } from '../tracks/provenance';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ExplicitToggle } from '../../components/ui/ExplicitToggle';
import { Field } from '../../components/ui/Field';
import { WaveformScan } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { pollUntil, uploadTrack } from '../../lib/upload';
import { ACCEPTED_AUDIO, contentTypeOf, describeFile, isAcceptedAudio } from '../upload/accepted';
import type { TrackSummary } from '../tracks/TrackList';
import type { Room } from './api';

/**
 * Uploading a track from inside the submit dialog, and sending it straight in.
 *
 * ## Why this is not a link to the upload page
 *
 * It was one - "You have not released anything yet" with a button that left the
 * room. Which is the correct answer to a different question: somebody who came
 * to a host with a file in their hand does not want a page, they want that file
 * in that queue. Sending them away means finding the room again afterwards,
 * remembering it was the point, and submitting a second time.
 *
 * So this is the shortest path that is still honest: choose a file, name it,
 * wait for the encoder, and land on the note step with the track already
 * chosen. The submission itself is unchanged - the dialog does that, with the
 * same rules and the same boost tiers.
 *
 * ## Fewer questions than the upload page, deliberately
 *
 * A file, a title, and two declarations. No genre, no description, no cover, no
 * visibility. Every one of those is editable afterwards from the track, and
 * asking for them here is asking somebody to fill in a form while a host is on
 * air.
 *
 * The line is between metadata and a *claim*. Rights, explicit and how the
 * record was made are all statements somebody is making about their own work,
 * and a statement nobody made is not a thing this form may supply on their
 * behalf - so those are asked even here, where everything else waits.
 *
 * Visibility is the exception, and it is not asked because there is only one
 * answer: a room can only play a public track, so offering the choice would be
 * offering a way to make the next screen fail.
 *
 * ## The wait is real and is described as such
 *
 * Encoding takes roughly as long as the track. Nothing here pretends
 * otherwise, and nothing hides the fact that closing the dialog part-way
 * leaves the upload finishing on its own - which is true, recoverable, and
 * better said than discovered.
 */

type Phase = 'choosing' | 'uploading' | 'processing' | 'failed' | 'timeout';

interface Status {
  id: number;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  failure_reason: string | null;
}

export function SubmitUpload({
  room,
  onReady,
}: {
  room: Room;
  /** The finished track, for the dialog to carry into the note step. */
  onReady: (track: TrackSummary) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [explicit, setExplicit] = useState(false);
  // No default: see `provenance.tsx`. An answer the form chose is not an
  // answer the artist gave.
  const [provenance, setProvenance] = useState<Provenance | ''>('');
  const [phase, setPhase] = useState<Phase>('choosing');
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const input = useRef<HTMLInputElement | null>(null);
  const abort = useRef<AbortController | null>(null);

  /**
   * Whether this dialog is still open.
   *
   * The submit handler outlives it: it polls for minutes, and everything after
   * each `await` runs whether or not anybody is still here. Read through a ref
   * because those continuations close over the render they started in.
   */
  const here = useRef(true);

  useEffect(() => {
    here.current = true;

    return () => {
      here.current = false;
      // Only the upload is cancellable. Once the bytes are up, the encoder is
      // going to finish whatever this dialog does.
      abort.current?.abort();
    };
  }, []);

  function choose(picked: File | null | undefined) {
    setFileError(null);

    if (!picked) return;

    if (!isAcceptedAudio(picked)) {
      setFileError(`We can't process that format. Try ${ACCEPTED_AUDIO.label}.`);

      return;
    }

    setFile(picked);
    // The filename minus its extension. Almost always close, and always easier
    // to correct than to type from nothing.
    setTitle((current) => current || picked.name.replace(/\.[^.]+$/, ''));
  }

  /*
   * Refused here rather than after the upload.
   *
   * `SubmitTrackAction` rejects an explicit track for a room that does not take
   * them, and finding that out is worth nothing three minutes into an encode.
   * The same rule, asked at the only moment it can still be acted on.
   */
  const refusedForExplicit = explicit && !room.accepts_explicit;

  async function start() {
    if (!file || phase === 'uploading' || phase === 'processing') return;

    setError(null);
    setPercent(0);
    setPhase('uploading');

    const controller = new AbortController();
    abort.current = controller;

    let id: number;

    try {
      id = await uploadTrack(
        file,
        {
          title: title.trim() || file.name,
          content_type: contentTypeOf(file),
          size_bytes: file.size,
          rights_confirmed: rights,
          // See the note at the top: a room can only play a public track.
          visibility: 'public',
          explicit,
          provenance: provenance as Provenance,
        },
        { onProgress: setPercent, signal: controller.signal },
      );
    } catch (caught) {
      abort.current = null;

      // Cancelling is not failing. Back to the form with the file intact.
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        setPhase('choosing');
        setPercent(0);

        return;
      }

      setError(
        caught instanceof ApiError || caught instanceof Error
          ? caught.message
          : 'The upload failed.',
      );
      setPhase('failed');

      return;
    }

    abort.current = null;
    setPhase('processing');

    const settled = await pollUntil<Status>(
      () => api.get<Status>(`/tracks/${id}`),
      (track) => track.status === 'ready' || track.status === 'failed',
      { intervalMs: 2000, timeoutMs: 300_000, abandoned: () => !here.current },
    );

    if (!here.current) return;

    if (!settled) {
      setPhase('timeout');

      return;
    }

    const track = await api.get<TrackSummary>(`/tracks/${id}`).catch(() => null);

    if (!here.current) return;

    if (track === null || track.status === 'failed') {
      setError(track?.failure_reason ?? "We couldn't process that file.");
      setPhase('failed');

      return;
    }

    /*
     * Straight to the note step with this track chosen, which is the whole
     * point of the tab. Anything else - a "done" screen, a list to pick it out
     * of again - is a step between somebody and the thing they came to do.
     */
    onReady(track);
  }

  /*
   * The same confirmation the upload page asks for, because this is the same
   * upload: a track dropped here lands in the catalog exactly as one dropped
   * there does. The server refuses either without it.
   */
  const [rights, setRights] = useState(false);

  const busy = phase === 'uploading' || phase === 'processing';

  if (busy) {
    return (
      <Card className="flex flex-col gap-4 p-5" data-submit-upload-progress>
        {phase === 'uploading' ? (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-ink">Uploading {file?.name}</span>
              <span className="meta-caps tabular-nums" data-upload-percent>
                {percent}%
              </span>
            </div>
            <div
              className="h-1.5 overflow-hidden rounded-[var(--radius-pill)] bg-line"
              role="progressbar"
              aria-label="Upload progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
            >
              <div
                className="h-full rounded-[var(--radius-pill)] bg-accent transition-[width] duration-200"
                style={{ width: `${percent}%` }}
              />
            </div>
            <Button variant="secondary" className="self-start" onClick={() => abort.current?.abort()}>
              Cancel
            </Button>
          </>
        ) : (
          <div className="flex items-start gap-4">
            {/*
              The scanning waveform rather than a spinner, for the reason the
              upload page gives: this wait is the encoder reading the file, and
              a lit segment travelling along a wave says that.
            */}
            <WaveformScan className="mt-0.5 w-24 shrink-0" />
            <div className="flex flex-col gap-1">
              <p className="text-sm text-ink">Your file is up. We're building the streaming version.</p>
              <p className="text-xs leading-relaxed text-faint">
                Roughly as long as the track itself, then it goes straight to the note. Closing this
                now does not lose it. It finishes on its own and waits on your profile.
              </p>
            </div>
          </div>
        )}
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-submit-upload>
      <div
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          choose(event.dataTransfer.files[0]);
        }}
        className="rounded-[var(--radius-card)] border border-dashed border-line-strong px-6 py-6 text-center"
        data-submit-dropzone
      >
        <input
          ref={input}
          type="file"
          accept={ACCEPTED_AUDIO.accept}
          className="sr-only"
          data-submit-file
          onChange={(event) => choose(event.target.files?.[0])}
        />

        {file ? (
          <>
            <p className="text-sm font-medium text-ink">{file.name}</p>
            <p className="meta-caps mt-1.5">{describeFile(file)}</p>
            <Button variant="ghost" className="mt-3" onClick={() => input.current?.click()}>
              Choose a different file
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-soft">Drag a file here</p>
            <p className="meta-caps mt-1">{ACCEPTED_AUDIO.label}, up to 500MB</p>
            <Button variant="secondary" className="mt-3" onClick={() => input.current?.click()}>
              Browse
            </Button>
          </>
        )}
      </div>

      {fileError && <Alert tone="error">{fileError}</Alert>}

      {file && (
        <>
          <Field
            label="Title"
            name="title"
            value={title}
            maxLength={120}
            required
            onChange={(event) => setTitle(event.target.value)}
          />

          <ProvenanceField id="submit-provenance" value={provenance} onChange={setProvenance} />

          <ExplicitToggle value={explicit} onChange={setExplicit} />

          {refusedForExplicit && (
            <Alert tone="error">
              {room.host.display_name} does not take tracks marked explicit. Untick it, or upload
              this one from your profile and send something else.
            </Alert>
          )}

          {/*
            Said before the upload, because it is the difference between this
            tab and the upload page and somebody should not have to notice it.
          */}
          <p className="text-xs leading-relaxed text-faint">
            It goes up public, which is the only thing a room can play. Artwork, genre and a
            description can be added afterwards from the track.
          </p>

          {/*
            The rights, per upload.

            Placed with the fields rather than beside the button, because it is
            a statement about this recording and not a step in a wizard - and
            the button will not go without it.
          */}
          <label className="flex cursor-pointer items-start gap-3 text-[13px] leading-relaxed text-soft">
            <input
              type="checkbox"
              checked={rights}
              data-confirm-rights
              onChange={(event) => setRights(event.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
            />
            <span>
              I have the right to upload this recording, and I have read{' '}
              <Link to="/artist-rights" target="_blank" className="text-accent hover:underline">
                Your music and your rights
              </Link>
              .
            </span>
          </label>
        </>
      )}

      {error && <Alert tone="error">{error}</Alert>}

      {phase === 'timeout' && (
        <Alert tone="info">
          Still processing. It has not failed. It will be on your profile when it is ready, and you
          can submit it from there.
        </Alert>
      )}

      <div className="flex items-center gap-3">
        <Button
          disabled={!file || !title.trim() || refusedForExplicit || !rights || provenance === ''}
          onClick={() => void start()}
          data-submit-upload-go
        >
          Upload and continue
        </Button>
      </div>
    </div>
  );
}
